import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, type Me, type SiteSummary } from '../lib/api';
import { Alert, Button, Card, EmptyState, Field, SectionTitle, Select, Spinner } from '../components/ui';
import { useStagger } from '../lib/motion';

export function SitesPage({ me }: { me: Me }) {
  const [sites, setSites] = useState<SiteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const listRef = useStagger<HTMLUListElement>('li', [sites?.length]);

  async function load() {
    try {
      const data = await api.sites();
      setSites(data.sites);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Laden fehlgeschlagen.');
      setSites([]);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div>
      <SectionTitle
        title="Websites"
        hint="Jede Website bekommt ein eigenes Script-Tag, eine eigene Cookie-Erklärung und ein eigenes Consent-Log."
        action={
          <Button onClick={() => setFormOpen((open) => !open)} size="sm">
            {formOpen ? 'Abbrechen' : 'Website hinzufügen'}
          </Button>
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      {formOpen && (
        <Card className="mb-5">
          <NewSiteForm
            organizations={me.organizations}
            onCreated={() => {
              setFormOpen(false);
              void load();
            }}
          />
        </Card>
      )}

      {sites === null ? (
        <div className="flex justify-center py-12 text-[var(--text-muted)]">
          <Spinner />
        </div>
      ) : sites.length === 0 ? (
        <EmptyState
          title="Noch keine Website angelegt"
          body="Lege die erste Website an, dann bekommst du sofort das Einbau-Snippet und kannst den ersten Scan starten."
          action={<Button onClick={() => setFormOpen(true)}>Website hinzufügen</Button>}
        />
      ) : (
        <ul ref={listRef} className="grid gap-3 sm:grid-cols-2">
          {sites.map((site) => (
            <li key={site.id}>
              <Link
                to={`/sites/${site.id}`}
                className="block h-full rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-5 transition-[border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-brand-400"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{site.name}</p>
                    <p className="tabular truncate text-sm text-[var(--text-muted)]">{site.domain}</p>
                  </div>
                  <span
                    className={
                      site.status === 'active'
                        ? 'rounded-full bg-ok-100 px-2.5 py-1 text-xs font-semibold text-ok-500'
                        : 'rounded-full bg-ink-100 px-2.5 py-1 text-xs font-semibold text-ink-600'
                    }
                  >
                    {site.status === 'active' ? 'aktiv' : 'pausiert'}
                  </span>
                </div>
                <dl className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[var(--text-muted)]">
                  <div>
                    <dt className="inline">Mandant: </dt>
                    <dd className="inline font-medium">{site.organizationName}</dd>
                  </div>
                  <div>
                    <dt className="inline">Letzter Scan: </dt>
                    <dd className="inline font-medium">
                      {site.lastScanAt ? new Date(site.lastScanAt).toLocaleDateString('de-DE') : 'noch keiner'}
                    </dd>
                  </div>
                </dl>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewSiteForm({
  organizations,
  onCreated,
}: {
  organizations: Me['organizations'];
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [organizationId, setOrganizationId] = useState(organizations[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vorhandeneSite, setVorhandeneSite] = useState<string | null>(null);
  const navigate = useNavigate();

  /**
   * Schutz vor doppeltem Abschicken.
   *
   * Der Button ist waehrend des Sendens deaktiviert, aber zwischen dem
   * Absenden und dem nächsten Rendern liegt ein Moment, in dem ein zweites
   * Enter durchkommt. Dann legt die erste Anfrage die Website an und die
   * zweite meldet "bereits vorhanden", was wie ein Fehlschlag aussieht.
   * Genau so ist es beim ersten produktiven Anlegen passiert.
   */
  const läuft = useRef(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (läuft.current) return;
    läuft.current = true;
    setBusy(true);
    setError(null);
    setVorhandeneSite(null);
    try {
      const { site } = await api.createSite({ organizationId, name, domain });
      onCreated();
      // Direkt zur neuen Website: dort steht das Einbau-Snippet, und der
      // Erfolg ist unmissverständlich sichtbar.
      navigate(`/sites/${site.id}`);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.code === 'domain_exists' && caught.siteId) setVorhandeneSite(caught.siteId);
      } else {
        setError('Anlegen fehlgeschlagen.');
      }
    } finally {
      läuft.current = false;
      setBusy(false);
    }
  }

  if (!organizations.length) {
    return <Alert tone="warn">Lege zuerst einen Mandanten an, dann kannst du Websites zuordnen.</Alert>;
  }

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
      {error && (
        <div className="sm:col-span-2">
          <Alert tone={vorhandeneSite ? 'warn' : 'error'}>
            {error}
            {vorhandeneSite && (
              <>
                {' '}
                <Link to={`/sites/${vorhandeneSite}`} className="font-semibold underline">
                  Zur vorhandenen Website
                </Link>
                <span className="mt-1 block text-xs">
                  Wenn du gerade zweimal abgeschickt hast: der erste Versuch war erfolgreich.
                </span>
              </>
            )}
          </Alert>
        </div>
      )}
      <Field label="Name" value={name} required onChange={(event) => setName(event.target.value)} placeholder="Muster GmbH" />
      <Field
        label="Domain"
        value={domain}
        required
        onChange={(event) => setDomain(event.target.value)}
        placeholder="muster-gmbh.de"
        hint="Ohne https:// und ohne www."
      />
      <Select label="Mandant" value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}>
        {organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </Select>
      <div className="flex items-end">
        <Button type="submit" loading={busy} className="w-full sm:w-auto">
          Anlegen
        </Button>
      </div>
    </form>
  );
}
